import React from 'react';
import { useParams } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { Spinner } from '@heroui/react';
import { NavBar } from '@/components/layout';
import { IconCheck as Check } from '@tabler/icons-react';

/**
 * 格式化为 YYYY-MM-DD HH:MM（分钟级精度，用于抽奖规则分组与展示）
 */
function formatDateTimeMinute(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

/**
 * 抽奖规则页面
 * 
 * 路由模式：/activities/:id/lottery-rules
 */
export default function LotteryRulesPage() {
  const { id: activityId } = useParams<{ id: string }>();
  const [current, setCurrent] = React.useState(0);

  // 查询所有奖品
  const { data: prizes, isLoading: prizesLoading } = trpc.activityPrizes.listAll.useQuery(
    {
      activityId: activityId!,
    },
    {
      enabled: !!activityId,
    }
  );

  // 查询所有抽奖规则，按 lotteryTime 从远到近排序（后端排序）
  const { data: lotteryRules, isLoading: rulesLoading } = trpc.activityLotteryRules.listAll.useQuery(
    {
      activityId: activityId!,
      sortBy: 'lotteryTime',
      sortOrder: 'asc', // 从远到近
    },
    {
      enabled: !!activityId,
    }
  );

  // 自动播放：每3秒切换一次
  React.useEffect(() => {
    if (!prizes || prizes.length <= 1) {
      return;
    }

    const autoplayTimer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % prizes.length);
    }, 3000); // 3秒间隔

    return () => {
      clearInterval(autoplayTimer);
    };
  }, [prizes]);


  // 按 lotteryTime 分组抽奖规则（相同时间的规则放在一组）
  const groupedRules = React.useMemo(() => {
    if (!lotteryRules || !prizes) return [];

    // 创建奖品映射
    const prizeMap = new Map(prizes.map((prize) => [prize.id, prize]));

    // 按完整的 lotteryTime 分组（相同时间的规则放在一组）
    const groups = new Map<string, typeof lotteryRules>();
    lotteryRules.forEach((rule) => {
      if (!rule.lotteryTime) return;
      // 使用分钟级精度作为分组 key（YYYY-MM-DD HH:MM）
      const timeKey = formatDateTimeMinute(rule.lotteryTime);
      if (!groups.has(timeKey)) {
        groups.set(timeKey, []);
      }
      groups.get(timeKey)!.push(rule);
    });

    // 转换为数组，按 lotteryTime 从远到近排序
    return Array.from(groups.entries())
      .map(([timeKey, rules]) => {
        // 使用第一个规则的时间来格式化显示（同一组的规则时间相同）
        const firstRule = rules[0];
        const displayTime = firstRule?.lotteryTime
          ? formatDateTimeMinute(firstRule.lotteryTime)
          : '';

        const mappedRules = rules.map((rule) => ({
          ...rule,
          prize: rule.prizeId ? prizeMap.get(rule.prizeId) : null,
        }));
        const allCompleted = mappedRules.every((r) => r.isCompleted === true);
        return {
          time: displayTime,
          rules: mappedRules,
          allCompleted,
        };
      })
      .sort((a, b) => {
        const timeA = a.rules[0]?.lotteryTime
          ? new Date(a.rules[0].lotteryTime).getTime()
          : 0;
        const timeB = b.rules[0]?.lotteryTime
          ? new Date(b.rules[0].lotteryTime).getTime()
          : 0;
        // 从远到近（时间小的在前）
        return timeA - timeB;
      });
  }, [lotteryRules, prizes]);

  const isLoading = prizesLoading || rulesLoading;

  if (isLoading) {
    return (
      <>
        <NavBar title="抽奖规则" showBack />
        <div className="flex items-center justify-center min-h-[60vh] pt-[var(--navbar-height,44px)]">
          <Spinner className="w-8 h-8" />
        </div>
      </>
    );
  }

  return (
    <>
      <NavBar
        title="抽奖规则"
        showBack
      />
      <div className="w-full pt-[var(--navbar-height,44px)]">
        {/* 1. 奖品轮播图 */}
        {prizes && prizes.length > 0 && (
          <div className="w-full px-4 py-6 bg-background">
            <div className="w-full">
              {(() => {
                const prize = prizes[current];
                return (
                  <div className="flex flex-col items-center space-y-3">
                    <div className="w-full aspect-square max-w-xs rounded-lg overflow-hidden bg-default-100">
                      {prize?.imageUrl ? (
                        <img
                          src={prize.imageUrl}
                          alt={prize.name || '奖品图片'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-default-500">
                          暂无图片
                        </div>
                      )}
                    </div>
                    <div className="text-center space-y-1">
                      <p className="text-base font-semibold">{prize?.name || '未命名奖品'}</p>
                      {prize?.quantity && (
                        <p className="text-sm text-default-500">
                          {prize.quantity}个
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
            {/* 分页指示器 */}
            {prizes.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {prizes.map((_, index) => (
                  <button
                    key={index}
                    className={`h-2 rounded-full transition-all ${
                      index === current
                        ? 'w-6 bg-primary'
                        : 'w-2 bg-default-400/30'
                    }`}
                    onClick={() => setCurrent(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* 2. 抽奖规则时间轴 */}
        {groupedRules.length > 0 && (
          <div className="px-4 pb-6">
            <div className="mt-4 space-y-4">
              {groupedRules.map((group, groupIndex) => (
                <div key={group.time} className="rounded-lg border border-default-200 bg-content1 p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-sm text-default-500">{groupIndex + 1}.</span>
                    <span className="text-sm font-medium">{group.time || '—'}</span>
                    {group.allCompleted ? <Check className="size-4 text-success-600" /> : null}
                  </div>
                  <div className="space-y-2">
                    {group.rules.map((rule, ruleIndex) => (
                      <div key={rule.id || ruleIndex} className="flex items-center justify-between py-1">
                        <span className="text-sm text-foreground">{rule.prize?.name || '未知奖品'}</span>
                        {rule.winnerCount ? (
                          <span className="text-sm text-default-500">{rule.winnerCount}个</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 空状态 */}
        {!isLoading && (!prizes || prizes.length === 0) && (!lotteryRules || lotteryRules.length === 0) && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
            <p className="text-default-500 text-center">暂无抽奖规则</p>
          </div>
        )}
      </div>
    </>
  );
}
